import { useState, useEffect, useCallback } from 'react';
import { Trash2, Mail, Phone, RefreshCw, Eye, X } from 'lucide-react';
import { Button } from '../ui/button';
import { API, axios, showToast } from './adminHelpers';

export const MessagesManager = ({ authHeader }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: authHeader });
      setMessages(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeader });
      showToast('Message deleted');
      fetchMessages();
      if (selectedMsg?.id === id) setSelectedMsg(null);
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  return (
    <div className="space-y-6" data-testid="messages-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Messages ({messages.length})</h1>
        <Button variant="outline" size="sm" onClick={fetchMessages} data-testid="refresh-messages-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">Loading...</div>
      ) : messages.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500" data-testid="no-messages-msg">No messages yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow divide-y divide-gray-200 overflow-hidden">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedMsg?.id === msg.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                onClick={() => setSelectedMsg(msg)}
                data-testid={`message-item-${msg.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 truncate">{msg.name}</span>
                      <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-700 truncate">{msg.subject}</div>
                    <div className="text-sm text-gray-500 truncate mt-1">{msg.message}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }} className="text-red-500 hover:text-red-700 ml-2 p-1" data-testid={`delete-msg-${msg.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {selectedMsg ? (
            <div className="bg-white rounded-lg shadow p-6" data-testid="message-detail">
              <div className="flex items-start justify-between mb-4">
                <h2 className="font-bold text-lg text-gray-900">{selectedMsg.subject}</h2>
                <button onClick={() => setSelectedMsg(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">From:</span>
                  <span className="font-medium">{selectedMsg.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${selectedMsg.email}`} className="text-blue-600 hover:underline">{selectedMsg.email}</a>
                </div>
                {selectedMsg.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${selectedMsg.phone}`} className="text-blue-600 hover:underline">{selectedMsg.phone}</a>
                  </div>
                )}
                <div className="text-xs text-gray-400">{new Date(selectedMsg.created_at).toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMsg.message}</p>
              </div>
              <div className="flex gap-2 mt-4">
                <a href={`mailto:${selectedMsg.email}?subject=Re: ${selectedMsg.subject}`} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="reply-email-btn">
                  <Mail className="w-4 h-4" /> Reply via Email
                </a>
                {selectedMsg.phone && (
                  <a href={`tel:${selectedMsg.phone}`} className="flex items-center justify-center gap-2 bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700" data-testid="call-msg-btn">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 flex flex-col items-center justify-center">
              <Eye className="w-12 h-12 mb-3 opacity-30" />
              <p>Select a message to view details</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
